import { ApiError, createApiClient } from '@rconf/api-client';
import { createAuthSession, createBrowserAuthAdapter } from '@rconf/auth-session';

const DEFAULT_HANDOFF = '/conferencing/meetings/';
const RBOT_REQUEST_PATH = '/api/auth/rbot/request';
const RBOT_VERIFY_PATH = '/api/auth/rbot/verify';

export function createAccountAuthRuntime(options = {}) {
  const fetchImpl = options.fetchImpl || globalThis.fetch;
  const adapter = createBrowserAuthAdapter({
    baseUrl: options.baseUrl || '',
    fetchImpl,
    credentials: 'include',
  });
  const session = createAuthSession({
    adapter,
    // The current Account API uses 30-minute opaque access tokens. Newer API
    // responses advertise the exact lifetime and take precedence over this fallback.
    accessTokenTtlSeconds: options.accessTokenTtlSeconds || 30 * 60,
  });
  const client = createApiClient({
    baseUrl: options.baseUrl || '',
    fetchImpl,
    credentials: 'include',
    ...session.apiClientOptions(),
  });
  const locationLike = options.location || globalThis.location;
  const navigate = options.navigate || ((url) => locationLike.replace(url));
  let bootstrapPromise = null;

  function restore() {
    if (!bootstrapPromise) {
      bootstrapPromise = session.restore().then((state) => {
        if (state?.error) bootstrapPromise = null;
        return state;
      });
    }
    return bootstrapPromise;
  }

  async function upload(path, body, onProgress) {
    const send = async () => uploadFormDataWithProgress({
      url: resolveRequestUrl(options.baseUrl || '', path),
      body,
      accessToken: await session.apiClientOptions().getAccessToken(),
      onProgress,
      xhrFactory: options.xhrFactory,
    });
    try {
      return await send();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      await session.refresh({ error });
      try {
        return await send();
      } catch (retryError) {
        if (retryError instanceof ApiError && retryError.status === 401) session.clear(retryError);
        throw retryError;
      }
    }
  }

  return Object.freeze({
    getState: session.getState,
    subscribe: session.subscribe,
    request: client.request,
    upload,
    restore,
    retryRestore() {
      bootstrapPromise = null;
      return restore();
    },
    loginWithPassword(credentials) {
      return session.login(credentials);
    },
    requestRendallCode(input) {
      return client.post(RBOT_REQUEST_PATH, input, { auth: false, retryAuth: false });
    },
    verifyRendallCode(input) {
      return session.login(input, { path: RBOT_VERIFY_PATH });
    },
    logout() {
      bootstrapPromise = null;
      return session.logout();
    },
    impersonate(userId) {
      return client.post(`/api/admin/users/${encodeURIComponent(String(userId || '').trim())}/impersonate`)
        .then(() => {
          session.clear();
          bootstrapPromise = null;
          return restore();
        });
    },
    stopImpersonation() {
      return client.post('/api/auth/impersonation/stop').then(() => {
        session.clear();
        bootstrapPromise = null;
        return restore();
      });
    },
    handoff() {
      const url = resolveAccountHandoff(options.handoffUrl, locationLike);
      navigate(url);
      return url;
    },
  });
}

export function uploadFormDataWithProgress({ url, body, accessToken, onProgress, xhrFactory }) {
  return new Promise((resolve, reject) => {
    const createXhr = xhrFactory || (() => new globalThis.XMLHttpRequest());
    let xhr;
    try {
      xhr = createXhr();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      if (accessToken) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
    } catch (cause) {
      reject(new ApiError('Не удалось начать загрузку файла.', { method: 'POST', url, cause, retryable: true }));
      return;
    }

    const report = typeof onProgress === 'function' ? onProgress : () => undefined;
    xhr.upload.addEventListener('progress', (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : 0;
      report({ loaded: event.loaded, total, percent: total ? Math.min(100, Math.round((event.loaded / total) * 100)) : null });
    });
    xhr.upload.addEventListener('load', () => report({ loaded: 0, total: 0, percent: 100 }));
    xhr.addEventListener('load', () => {
      const responseBody = parseUploadResponse(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(responseBody);
        return;
      }
      reject(uploadApiError(xhr, responseBody, url));
    });
    xhr.addEventListener('error', () => reject(new ApiError('Не удалось передать файл. Проверьте соединение и повторите попытку.', {
      status: 0,
      code: 'NETWORK_ERROR',
      method: 'POST',
      url,
      retryable: true,
    })));
    xhr.addEventListener('abort', () => reject(new ApiError('Загрузка файла отменена.', {
      status: 0,
      code: 'UPLOAD_ABORTED',
      method: 'POST',
      url,
      retryable: true,
    })));
    xhr.send(body);
  });
}

function resolveRequestUrl(baseUrl, path) {
  const requestPath = String(path || '').trim();
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(requestPath)) {
    throw new TypeError('Absolute request URLs are disabled to prevent credential leakage');
  }
  const base = String(baseUrl || '').trim().replace(/\/+$/, '');
  if (!base) return requestPath || '/';
  return `${base}/${requestPath.replace(/^\/+/, '')}`;
}

function parseUploadResponse(text) {
  const value = String(text || '');
  if (!value) return null;
  try { return JSON.parse(value); } catch { return value; }
}

function uploadApiError(xhr, responseBody, url) {
  const detail = responseBody && typeof responseBody === 'object' ? responseBody.detail : null;
  const message = typeof detail === 'string'
    ? detail
    : String(detail?.message || responseBody?.message || `Ошибка загрузки (${xhr.status})`);
  return new ApiError(message, {
    status: xhr.status,
    code: String(detail?.code || (xhr.status ? 'HTTP_ERROR' : 'NETWORK_ERROR')),
    requestId: String(xhr.getResponseHeader?.('x-request-id') || ''),
    method: 'POST',
    url,
    details: detail,
    responseBody,
  });
}

export function resolveAccountHandoff(value, locationLike = globalThis.location) {
  const origin = safeOrigin(locationLike);
  try {
    const target = new URL(String(value || DEFAULT_HANDOFF), `${origin}/`);
    if (target.origin !== origin) return DEFAULT_HANDOFF;
    if (!target.pathname.startsWith('/')) return DEFAULT_HANDOFF;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return DEFAULT_HANDOFF;
  }
}

export function initialAuthMethod(search = '') {
  const method = new URLSearchParams(String(search || '')).get('auth');
  return method === 'rbot' || method === 'rendall' ? 'rendall' : 'password';
}

function safeOrigin(locationLike) {
  const value = String(locationLike?.origin || '').trim();
  if (/^https?:\/\//i.test(value)) return value.replace(/\/+$/, '');
  return 'https://account.rconf.local';
}
